import { BookOpen, Download, GraduationCap, Keyboard, LogOut, Menu as MenuIcon, Monitor, Moon, PanelLeftClose, PanelLeftOpen, Plus, Search, Settings, Sun, WifiOff } from 'lucide-react';
import { forwardRef, type RefObject, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useLogout } from '../api/auth';
import { ORG_ROLE_LABELS } from '../api/org';
import { useStreamStatus } from '../api/stream';
import { Avatar } from '../components/ui/Avatar';
import { Kbd } from '../components/ui/Badge';
import { Button, IconButton } from '../components/ui/Button';
import { Menu, useMenu } from '../components/ui/Menu';
import { Tip } from '../components/ui/Tooltip';
import { cn } from '../lib/cn';
import { useOnline } from '../lib/hooks';
import { useSession } from './session';
import { useShell } from './shell-context';
import { ShellTopbarSlots } from './TaskExtensions';
import { useTraining } from '../features/training/TrainingProvider';
import { useInstallPrompt } from './pwa';
import { THEME_LABELS, type ThemePref, useTheme } from './theme';

const THEME_ICONS: Record<ThemePref, typeof Sun> = { system: Monitor, light: Sun, dark: Moon };

export const TopbarSearch = forwardRef<HTMLInputElement, { className?: string }>(function TopbarSearch({ className }, ref) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <form
      role="search"
      data-tour="topbar-search"
      className={cn('relative w-full max-w-[420px]', className)}
      onSubmit={(e) => {
        e.preventDefault();
        navigate(`/search${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);
        (ref as RefObject<HTMLInputElement | null> | null)?.current?.blur();
      }}
    >
      <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-fg-3" aria-hidden />
      <input
        ref={ref}
        type="search"
        enterKeyHint="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setQ('');
            e.currentTarget.blur();
          }
        }}
        aria-label="Пошук задач, проєктів і працівників"
        aria-keyshortcuts="/"
        placeholder="Пошук: задача, номер Т-…, проєкт, людина"
        className="h-8 w-full rounded-sm border border-line bg-surface-2 pr-9 pl-8 text-body text-fg outline-none transition-colors placeholder:text-fg-3 hover:border-line-strong focus:border-focus focus:bg-surface focus:ring-2 focus:ring-focus/20"
      />
      <Kbd className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2">/</Kbd>
    </form>
  );
});

export function Topbar({
  onOpenNav,
  searchRef,
  sidebarCollapsed,
  onToggleSidebar,
}: {
  onOpenNav: () => void;
  searchRef: RefObject<HTMLInputElement | null>;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
}) {
  const { me } = useSession();
  const shell = useShell();
  const theme = useTheme();
  const logout = useLogout();
  const navigate = useNavigate();
  const profile = useMenu();
  const themeMenu = useMenu();
  const online = useOnline();
  const stream = useStreamStatus();
  const training = useTraining();
  const installer = useInstallPrompt();
  const ThemeIcon = THEME_ICONS[theme.pref];

  return (
    <header className="ers-chrome flex h-[var(--topbar-h)] shrink-0 items-center gap-2 border-b border-line bg-canvas text-fg px-2 pt-[env(safe-area-inset-top)] sm:px-4">
      <IconButton label="Меню навігації" className="lg:hidden" onClick={onOpenNav}>
        <MenuIcon className="size-5" aria-hidden />
      </IconButton>
      <span className="hidden lg:inline-flex">
        <IconButton label={sidebarCollapsed ? 'Розгорнути бічну панель' : 'Згорнути бічну панель'} onClick={onToggleSidebar} aria-pressed={!sidebarCollapsed}>
          {sidebarCollapsed ? <PanelLeftOpen className="size-[18px]" aria-hidden /> : <PanelLeftClose className="size-[18px]" aria-hidden />}
        </IconButton>
      </span>
      <Link to="/my-work" aria-label="Єдина робоча система — до «Моєї роботи»" title="До «Моєї роботи»" className={cn('flex min-w-0 items-center gap-2 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-focus md:shrink-0', !sidebarCollapsed && 'lg:hidden')}>
        {/* На широкому екрані логотип уже є в бічній панелі (і в згорнутій смузі) — тут лише назва. */}
        <span className="flex size-6 shrink-0 items-center justify-center rounded-xs bg-accent text-meta font-semibold text-on-accent lg:hidden" aria-hidden>
          Є
        </span>
        <span className="truncate text-body font-semibold">Єдина робоча система</span>
      </Link>
      <TopbarSearch ref={searchRef} className="hidden md:block" />
      <span className="ml-auto flex items-center gap-1">
        {(!online || stream === 'reconnecting') && (
          <Tip content={!online ? 'Немає з’єднання з мережею' : 'Оновлення в реальному часі відновлюється'}>
            <span className={cn('flex items-center gap-1.5 rounded-sm px-2 py-1 text-caption', !online ? 'bg-warning-soft text-warning-fg' : 'text-fg-3')}>
              <WifiOff className="size-3.5" aria-hidden />
              <span className="hidden sm:inline">{!online ? 'Офлайн' : 'Перепідключення'}</span>
            </span>
          </Tip>
        )}
        <IconButton label="Пошук" className="md:hidden" onClick={() => navigate('/search')} data-tour="topbar-search">
          <Search className="size-5" aria-hidden />
        </IconButton>
        <ShellTopbarSlots />
        {/* На телефоні задачу створюють з нижньої панелі; обгортка, бо клас display кнопки перекрив би hidden. */}
        <span className="hidden sm:inline-flex" data-tour="topbar-new-task">
          <Button variant="primary" size="sm" icon={<Plus className="size-4" aria-hidden />} onClick={() => shell.openCreateTask()} aria-keyshortcuts="q">
            Нова задача
            <Kbd tone="accent" className="ml-1">
              Q
            </Kbd>
          </Button>
        </span>
        <IconButton label="Тема оформлення" {...themeMenu.triggerProps}>
          <ThemeIcon className="size-[18px]" aria-hidden />
        </IconButton>
        <Menu
          open={themeMenu.open}
          onClose={themeMenu.close}
          anchor={themeMenu.anchor}
          label="Тема оформлення"
          placement="bottom-end"
          items={(['system', 'light', 'dark'] as ThemePref[]).map((p) => {
            const I = THEME_ICONS[p];
            return { type: 'radio' as const, key: p, label: THEME_LABELS[p], checked: theme.pref === p, icon: <I className="size-4" />, onSelect: () => theme.setPref(p) };
          })}
        />
        <button
          type="button"
          aria-label={`Профіль: ${me.user.name}`}
          className="ml-1 inline-flex size-8 items-center justify-center rounded-full hover:ring-2 hover:ring-line-strong"
          {...profile.triggerProps}
        >
          <Avatar name={me.user.name} src={me.user.avatarUrl} size={28} />
        </button>
        <Menu
          open={profile.open}
          onClose={profile.close}
          anchor={profile.anchor}
          label="Профіль"
          placement="bottom-end"
          className="min-w-[260px]"
          items={[
            { type: 'label', key: 'who', label: `${me.user.name} · ${ORG_ROLE_LABELS[me.user.role]}` },
            { key: 'settings', label: 'Налаштування', icon: <Settings className="size-4" />, onSelect: () => navigate('/settings') },
            { key: 'keys', label: 'Гарячі клавіші', icon: <Keyboard className="size-4" />, shortcut: '?', onSelect: () => shell.openShortcuts() },
            {
              key: 'training',
              label: training.enabled ? 'Вимкнути режим навчання' : 'Режим навчання',
              description: training.enabled ? undefined : 'Підказки «?» і тури екранами',
              icon: <GraduationCap className="size-4" />,
              onSelect: () => training.toggle(),
            },
            ...(installer.canInstall || (installer.iosHint && !installer.installed)
              ? [
                  {
                    key: 'install',
                    label: 'Встановити застосунок',
                    icon: <Download className="size-4" />,
                    onSelect: () => (installer.canInstall ? void installer.install() : navigate('/settings?tab=app')),
                  },
                ]
              : []),
            { key: 'docs', label: 'Документація API', icon: <BookOpen className="size-4" />, onSelect: () => window.open('/api/docs', '_blank', 'noopener') },
            { type: 'separator', key: 's' },
            {
              key: 'logout',
              label: 'Вийти',
              icon: <LogOut className="size-4" />,
              // Офлайн-дані, чергу відкладених дій і стан екранів прибирає сам вихід (useLogout) — ще до запиту на сервер.
              onSelect: () => logout.mutate(undefined, { onSettled: () => navigate('/login', { replace: true }) }),
            },
          ]}
        />
      </span>
    </header>
  );
}
